import React, { useContext, useReducer, useRef } from 'react'
import { PARTNERS, PAYMENT_REQUESTS, PURCHASES, USERS } from '../../constants/collections'
import { fetchCollection, fetchDocument, retrieveCustomers, sendPurchaseOrderReminder, updateDocument } from '../../services/firebase'
import { ADD_PAYMENT_REQUEST, SET_CUSTOMERS, SET_PARTNERS, SET_PAYMENT_REQUESTS, SET_PURCHASES, SET_PURCHASES_LOADING, SET_PURCHASE_ORDERS } from '../types'
import customersReducer from './customersReducer'
import { NotificationsContext } from '../notifications/notificationsState'

const CustomersContext = React.createContext()

// eslint-disable-next-line react/prop-types
const CustomersState = ({ children }) => {

  const initialState = {
    customers: [],
    customersLoaded: false,
    purchases: [],
    purchasesLoaded: false,
    purchasesLoading: false,
    purchaseOrders: [],
    purchaseOrdersLoaded: false,
    paymentRequests: [],
    paymentRequestsLoaded: false,
    partners: [],
    partnersLoaded: false,
    userSegments: [],
    userSegmentsLoaded: false,
  }

  const [state, dispatch] = useReducer(customersReducer, initialState)
  const purchasesSeasonCacheRef = useRef({})
  const { toast } = useContext(NotificationsContext)

  const fetchCustomers = async () => {
    const users = await retrieveCustomers()
    const obj = {}
    for(let u of users) {
      obj[u.uid] = u
    }
    const snapshot = await fetchCollection(USERS)
    snapshot.forEach(doc => {
      if(obj[doc.id]) {
        obj[doc.id] = {
          ...obj[doc.id],
          ...doc.data(),
        }
      }
    })
    const array = Object.values(obj)
    setCustomers(array)
  }

  const fetchPurchases = async ({ rangeStart, rangeEnd, seasonKey, isMostRecent } = {}) => {
    if(state.purchasesLoading) {
      return
    }
    if(seasonKey) {
      const cacheEntry = purchasesSeasonCacheRef.current[seasonKey]
      if(cacheEntry?.loaded && !isMostRecent) {
        setPurchases(cacheEntry.purchases || [])
        return
      }
    }
    dispatch({
      type: SET_PURCHASES_LOADING,
      payload: true
    })
    try {
      const queries = [{ field: "status", op: "in", value: ["paid", "refunded"] }]
      const cacheEntry = seasonKey ? purchasesSeasonCacheRef.current[seasonKey] : null
      const shouldFetchIncremental = Boolean(seasonKey && isMostRecent && cacheEntry?.loaded && cacheEntry?.latestPaidAt)
      if(shouldFetchIncremental) {
        if(rangeStart && cacheEntry.latestPaidAt < rangeStart) {
          queries.push({ field: "paid_at", op: ">=", value: rangeStart })
        } else {
          queries.push({ field: "paid_at", op: ">", value: cacheEntry.latestPaidAt })
        }
      } else if(rangeStart) {
        queries.push({ field: "paid_at", op: ">=", value: rangeStart })
      }
      if(rangeEnd) {
        queries.push({ field: "paid_at", op: "<=", value: rangeEnd })
      }
      const snapshot = await fetchCollection(PURCHASES, queries)
      const array = []
      snapshot.forEach(doc => {
        array.push({
          ...doc.data(),
          id: doc.id
        })
      })
      let nextPurchases = array
      if(shouldFetchIncremental) {
        const basePurchases = cacheEntry?.purchases || state.purchases
        const merged = new Map(basePurchases.map(p => [p.id, p]))
        array.forEach(purchase => {
          merged.set(purchase.id, purchase)
        })
        nextPurchases = Array.from(merged.values())
      }
      setPurchases(nextPurchases)
      if(seasonKey) {
        const latestPaidAt = nextPurchases.reduce((maxPaidAt, purchase) => {
          const paidAt = purchase.paid_at || 0
          return paidAt > maxPaidAt ? paidAt : maxPaidAt
        }, 0)
        purchasesSeasonCacheRef.current[seasonKey] = {
          loaded: true,
          latestPaidAt,
          purchases: nextPurchases
        }
      }
    } finally {
      dispatch({
        type: SET_PURCHASES_LOADING,
        payload: false
      })
    }
  }

  const fetchPaymentRequests = async () => {
    const snapshot = await fetchCollection(PAYMENT_REQUESTS, { field: "status", op: "in", value: ["paid", "refunded"] })
    const array = []
    snapshot.forEach(doc => {
      array.push({
        ...doc.data(),
        id: doc.id
      })
    })
    dispatch({
      type: SET_PAYMENT_REQUESTS,
      payload: array
    })
  }

  const fetchPartners = async () => {
    const snapshot = await fetchCollection(PARTNERS)
    const array = []
    snapshot.forEach(doc => {
      array.push({
        ...doc.data(),
        id: doc.id
      })
    })
    setPartners(array)
  }

  const fetchPaymentRequest = async (id) => {
    const doc = await fetchDocument(`${PAYMENT_REQUESTS}/${id}`)
    const requestData = {
      ...doc.data(),
      id: doc.id
    }
    dispatch({
      type: ADD_PAYMENT_REQUEST,
      payload: requestData
    })
    return requestData
  }

  const setCustomers = (data) => {
    dispatch({
      type: SET_CUSTOMERS,
      payload: data
    })
  }

  const setPurchases = (data) => {
    dispatch({
      type: SET_PURCHASES,
      payload: data
    })
  }

  const setSinglePurchase = (id, data) => {
    let found = false
    const purchases = state.purchases.map(p => {
      if(p.id === id) {
        p = { ...p, ...data }
        found = true
      }
      return p
    })
    if(!found) {
      purchases.push(data)
    }
    setPurchases(purchases)
  }

  const fetchPurchaseOrders = async () => {
    const snapshot = await fetchCollection(PAYMENT_REQUESTS, { field: "payment_method", op: "==", value: "bank-transfer" })
    const array = []
    snapshot.forEach(doc => {
      array.push({
        ...doc.data(),
        id: doc.id
      })
    })
    setPurchaseOrders(array)
  }

  const setPurchaseOrders = (data) => {
    dispatch({
      type: SET_PURCHASE_ORDERS,
      payload: data
    })
  }

  const setSinglePurchaseOrder = (id, data) => {
    setPurchaseOrders(state.purchaseOrders.map(order => order.id === id ? { ...order, ...data, id } : order))
  }

  const setPartners = (data) => {
    dispatch({
      type: SET_PARTNERS,
      payload: data
    })
  }

  const markPurchaseOrderPaid = async (orderId) => {
    await updateDocument(`${PAYMENT_REQUESTS}/${orderId}`, { status: "paid", paid_at: Date.now() })
    const orders = state.purchaseOrders.map(o => {
      if(o.id === orderId) {
        o.status = "paid"
        o.paid_at = Date.now()
      }
      return o
    })
    setPurchaseOrders(orders)
  }

  const markPurchaseOrderCancelled = async (orderId) => {
    await updateDocument(`${PAYMENT_REQUESTS}/${orderId}`, { status: "cancelled" })
    const orders = state.purchaseOrders.map(o => {
      if(o.id === orderId) {
        o.status = "cancelled"
      }
      return o
    })
    setPurchaseOrders(orders)
  }

  const remindPurchaseOrder = async (orderId) => {
    const response = await sendPurchaseOrderReminder(orderId)
    if(!response.reminders) {
      toast({ message: `Sending reminder failed`, type: 'danger' })
      return false
    }
    const orders = state.purchaseOrders.map(o => {
      if(o.id === orderId) {
        o.reminders = response.reminders
      }
      return o
    })
    setPurchaseOrders(orders)
    toast({ message: `Reminder sent`, type: 'success' })
  }

  const fetchUserSegments = async () => {
    // TODO: Implement user segments fetching logic
  }

  return (
    <CustomersContext.Provider value={{
      customers: state.customers,
      customersLoaded: state.customersLoaded,
      purchases: state.purchases,
      purchasesLoaded: state.purchasesLoaded,
      purchasesLoading: state.purchasesLoading,
      purchaseOrders: state.purchaseOrders,
      purchaseOrdersLoaded: state.purchaseOrdersLoaded,
      paymentRequests: state.paymentRequests,
      paymentRequestsLoaded: state.paymentRequestsLoaded,
      partners: state.partners,
      partnersLoaded: state.partnersLoaded,
      userSegments: state.userSegments,
      userSegmentsLoaded: state.userSegmentsLoaded,
      fetchCustomers,
      fetchPurchases,
      fetchPurchaseOrders,
      setSinglePurchaseOrder,
      markPurchaseOrderPaid,
      markPurchaseOrderCancelled,
      fetchPaymentRequest,
      setSinglePurchase,
      remindPurchaseOrder,
      fetchPaymentRequests,
      fetchPartners,
      fetchUserSegments
    }}>
      { children }
    </CustomersContext.Provider>
  )
}

export { CustomersContext }

export default CustomersState
